import type { HtmlAttributes } from '@studio/design-system/props';
import type { Settings } from '@studio/models/video';
import { VideoDefaults } from '@studio/models/video';
import { LocalDefaults } from '@studio/models/video/local';
import type { Configuration } from '@studio/models/video/local';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import type { T_Nullable } from '@tryiris-ai/js-types';
import { defaultsDeep } from 'lodash-es';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { Local as _Local } from '#~/video/components/editor/index.js';

/** @internal */
const Inner = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  const settings = useStore(
    ({ candidate }) =>
      // We know this is a normalized NDI configuration.
      candidate.video as Settings<Configuration>,
    shallow
  );

  const handleChange = useLatestFn((value: Settings) => {
    producer((draft) => {
      draft.candidate.video = value;
    });
  });

  return <_Local settings={toAccessor(settings, handleChange)} {...props} />;
};

/** NDI settings. */
export const Local = (props: HtmlAttributes): T_Nullable<ReactElement> => {
  const [isPrimed, setPrimed] = useState(false);
  const producer = useProducer();

  // On mount, normalize the NDI video settings.
  useMount(() => {
    queueMicrotask(() => {
      producer(({ candidate }) => {
        const { video } = candidate;

        // Normalize configuration.
        candidate.video = defaultsDeep(
          { ...video },
          LocalDefaults,
          VideoDefaults
        ) as Settings<Configuration>;
      });

      setPrimed(true);
    });
  });

  return isPrimed ? <Inner {...props} /> : null;
};
